<script setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { ShieldCheck, KeyRound, Pen, Eye, Info, Check } from "lucide-vue-next";

import {
  getCurrentSignParticipant,
  signParticipant,
  signatureValidate,
} from "@/entities/transaction";
import nclayerPort from "@/shared/nclayer";
import {
  getClientIP,
  generateDeviceFingerprint,
  getUserAgent,
} from "@/shared/lib/signatureHelpers";
import { toast } from "@/shared/store/alertStore";
import { SignerParticipantStatus } from "@/shared/config/signerParticipantStatuses";

import Button from "@/shared/ui/button/Button.vue";
import { Spinner } from "@/shared/ui/spinner";
import PreviewTipTap2 from "@/features/document-editor/ui/PreviewTipTap2.vue";
import SignaturePad from "./SignaturePad.vue";

const props = defineProps({
  uuid: { type: String, required: true },
  editable: { type: Boolean, default: false },
  form: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["signed"]);

const { t } = useI18n();

const isLoadingSigner = ref(false);
const isSubmitting = ref(false);
const consent = ref(false);
const method = ref("ecp");
const hasInk = ref(false);
const padRef = ref(null);

const previewRef = ref(null);

const currentSignerData = ref(null);
const currentSigner = ref(null);
const signaturesInfo = ref({ signed: 0, remaining: 0, total: 0 });

const myParticipantUuid = computed(() => currentSigner.value?.uuid || null);

const allSigned = computed(
  () =>
    !currentSigner.value &&
    signaturesInfo.value.remaining === 0 &&
    signaturesInfo.value.total > 0
);

const alreadySigned = computed(
  () =>
    currentSigner.value?.status === SignerParticipantStatus.SIGNED ||
    allSigned.value
);

const canSign = computed(
  () =>
    consent.value &&
    (method.value === "ecp" || (method.value === "hand" && hasInk.value))
);

const docName = computed(
  () => props.form?.template_name || t("Заявление о нотариальной надписи")
);

const docMeta = computed(() => {
  const lang = (props.form?.languages || ["ru"])[0];
  const langLabel = lang === "kz" ? "KZ" : lang === "en" ? "EN" : "RU";
  return `${langLabel} · ${t("документ")}`;
});

const previewJson = computed(() => {
  const lang = (props.form?.languages || ["ru"])[0];
  const key =
    lang === "kz" ? "content_kz" : lang === "en" ? "content_en" : "content";
  return props.form?.[key] || null;
});

const statusText = computed(() => {
  if (alreadySigned.value) return t("Документ уже подписан");
  if (canSign.value) return t("Готово к подписанию");
  if (!consent.value) return t("Подтвердите согласие, чтобы подписать");
  if (method.value === "hand" && !hasInk.value) {
    return t("Распишитесь в области выше");
  }
  return t("Заполните все поля");
});

function openPreview() {
  previewRef.value?.open({ item: previewJson.value });
}

async function loadCurrentSigner() {
  if (!props.uuid) return;
  isLoadingSigner.value = true;
  try {
    const data = await getCurrentSignParticipant(props.uuid);
    if (data?.success && data.payload) {
      currentSignerData.value = data.payload;
      currentSigner.value = data.payload.current_participant;
      signaturesInfo.value = {
        signed: data.payload.signatures?.signed || 0,
        remaining: data.payload.signatures?.remaining || 0,
        total: data.payload.signatures?.total || 0,
      };
    }
  } catch (e) {
    console.error("[InscriptionSignBlock] loadCurrentSigner:", e);
    currentSigner.value = null;
  } finally {
    isLoadingSigner.value = false;
  }
}

async function submitSignature(signatureData, algorithm) {
  const participantUuid = myParticipantUuid.value;
  if (!participantUuid) {
    toast.error(t("Не найден UUID участника"));
    return;
  }
  const params = {
    participant_uuid: participantUuid,
    algorithm: algorithm || "SHA256",
    signature_data: signatureData,
    ip_address: await getClientIP(),
    user_agent: getUserAgent(),
    device_fingerprint: generateDeviceFingerprint(),
  };
  const result = await signParticipant(props.uuid, params);
  if (result?.success && result.payload) {
    if (result.payload.signature_uuid) {
      try {
        await signatureValidate(result.payload.signature_uuid);
      } catch (e) {
        console.error("[InscriptionSignBlock] signatureValidate:", e);
      }
    }
    await loadCurrentSigner();
    emit("signed", result);
  } else {
    const msg =
      result?.error?.message ||
      result?.message ||
      t("Не удалось подписать документ");
    toast.error(msg);
  }
}

async function handleSign() {
  if (!props.editable || alreadySigned.value || !canSign.value) return;
  if (isSubmitting.value) return;
  if (!myParticipantUuid.value) {
    toast.error(t("Не найден UUID участника"));
    return;
  }
  isSubmitting.value = true;
  try {
    if (method.value === "ecp") {
      const transactionHash =
        currentSignerData.value?.transaction_hash || String(props.uuid);
      const base64Data = btoa(unescape(encodeURIComponent(transactionHash)));
      let signature;
      try {
        const result = await nclayerPort.sign(base64Data);
        signature = result?.signatures?.[0];
      } catch (e) {
        toast.error(
          e?.message ||
            t(
              "Не удалось подписать через NCALayer. Запустите NCALayer и попробуйте снова."
            )
        );
        return;
      }
      if (!signature) {
        toast.error(t("Не получена подпись от NCALayer"));
        return;
      }
      await submitSignature(signature, "SHA256");
    } else {
      const dataURL = padRef.value?.getDataURL();
      if (!dataURL) {
        toast.error(t("Распишитесь в области выше"));
        return;
      }
      await submitSignature(dataURL, "BASE64");
    }
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      e?.message ||
      t("Ошибка при подписании документа");
    toast.error(msg);
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(loadCurrentSigner);
</script>

<template>
  <div class="sign-panel">
    <div class="head">
      <span class="head-icon">
        <ShieldCheck class="w-[18px] h-[18px]" />
      </span>
      <div class="head-text">
        <h3 class="title">{{ $t("Подписание заявления") }}</h3>
        <p class="sub">
          {{
            $t(
              "Выберите способ подписи. После подписания заявление уйдёт нотариусу."
            )
          }}
        </p>
      </div>
    </div>

    <div v-if="isLoadingSigner" class="body loader-body">
      <Spinner size="sm" />
      <span>{{ $t("Загрузка...") }}</span>
    </div>

    <template v-else>
      <div class="body">
        <!-- Doc preview -->
        <div class="doc-preview">
          <div class="doc-thumb" aria-hidden="true">
            <span class="doc-line" />
            <span class="doc-line" />
            <span class="doc-line" />
            <span class="doc-line" />
            <span class="doc-line" />
          </div>
          <div class="doc-main">
            <div class="doc-name">{{ docName }}</div>
            <div class="doc-meta">{{ docMeta }}</div>
          </div>
          <button class="doc-view" type="button" @click="openPreview">
            <Eye class="w-[13px] h-[13px]" />
            <div>{{ $t("Просмотр") }}</div>
          </button>
        </div>

        <!-- Method selector -->
        <div class="method-section">
          <div class="method-label">{{ $t("Способ подписи") }}</div>
          <div class="method-grid">
            <button
              type="button"
              class="method-card"
              :data-active="method === 'ecp'"
              :disabled="alreadySigned || !editable"
              @click="method = 'ecp'"
            >
              <span class="method-icon">
                <KeyRound class="w-[18px] h-[18px]" />
              </span>
              <span class="method-main">
                <span class="method-title">
                  {{ $t("ЭЦП через NCALayer") }}
                </span>
                <span class="method-sub">
                  {{ $t("Файл электронной цифровой подписи") }}
                </span>
              </span>
              <span class="method-radio" />
            </button>
            <button
              type="button"
              class="method-card"
              :data-active="method === 'hand'"
              :disabled="alreadySigned || !editable"
              @click="method = 'hand'"
            >
              <span class="method-icon">
                <Pen class="w-[18px] h-[18px]" />
              </span>
              <span class="method-main">
                <span class="method-title">{{ $t("Подпись рукой") }}</span>
                <span class="method-sub">
                  {{ $t("Распишитесь мышью или пальцем") }}
                </span>
              </span>
              <span class="method-radio" />
            </button>
          </div>
        </div>

        <SignaturePad
          v-if="method === 'hand'"
          ref="padRef"
          @change="(v) => (hasInk = v)"
        />

        <!-- Consent -->
        <label class="consent" :data-checked="consent">
          <input
            v-model="consent"
            type="checkbox"
            class="consent-input"
            :disabled="isSubmitting || !editable || alreadySigned"
          />
          <div class="consent-box">
            <Check v-if="consent" class="w-3 h-3" stroke-width="3" />
          </div>
          <div class="consent-text">
            {{ $t("Я подтверждаю, что") }}
            <strong>{{ $t("ознакомлен с содержанием") }}</strong>
            {{ $t("заявления и согласен подписать его") }}
            {{
              method === "ecp"
                ? $t("электронной цифровой подписью")
                : $t("от руки")
            }}.
          </div>
        </label>
      </div>

      <!-- Footer -->
      <div class="foot">
        <span class="status">
          <span v-if="canSign && !alreadySigned" class="status-dot" />
          <Info v-else class="w-[13px] h-[13px]" />
          {{ statusText }}
        </span>
        <Button
          variant="default"
          :disabled="!editable || alreadySigned || isSubmitting || !canSign"
          @click="handleSign"
        >
          <Spinner v-if="isSubmitting" size="sm" class="mr-2" />
          <component
            :is="method === 'ecp' ? KeyRound : Pen"
            v-else
            class="w-[14px] h-[14px] mr-2"
          />
          {{
            isSubmitting
              ? $t("Подписание…")
              : method === "ecp"
              ? $t("Подписать через ЭЦП")
              : $t("Подписать")
          }}
        </Button>
      </div>
    </template>

    <!-- Preview -->
    <PreviewTipTap2
      ref="previewRef"
      :template-lang="(form?.languages || ['ru'])[0]"
      :template-json="previewJson || {}"
    />
  </div>
</template>

<style scoped lang="scss">
.sign-panel {
  @apply h-full min-h-0 bg-card border border-border rounded-[var(--radius)]
    overflow-hidden flex flex-col;

  @media (max-width: 1500px) {
    @apply w-full;
  }
}

.head {
  @apply flex items-start gap-3 p-5;
}

.head-icon {
  @apply w-9 h-9 rounded-[var(--radius)] bg-muted border border-border
    flex items-center justify-center text-foreground shrink-0;
}

.head-text {
  @apply flex-1 min-w-0;
}

.title {
  @apply text-[15px] font-semibold text-foreground leading-5 m-0;
}

.sub {
  @apply text-[12.5px] text-muted-foreground leading-5 m-0 mt-1;
}

.body {
  @apply flex-1 min-h-0 overflow-y-auto flex flex-col gap-[18px] p-5;
}

.loader-body {
  @apply flex-row items-center justify-center py-10 text-sm text-muted-foreground;
}

/* Doc preview */
.doc-preview {
  @apply flex items-center gap-[14px] p-[14px] rounded-[var(--radius)] border border-border bg-muted/40;
}

.doc-thumb {
  @apply w-11 rounded-[var(--radius)] bg-card border border-border flex flex-col p-[5px] gap-[2px] shrink-0 overflow-hidden;
  height: 48px;
}

.doc-line {
  @apply h-[2px] rounded bg-border;

  &:nth-child(1) {
    width: 80%;
  }
  &:nth-child(2) {
    width: 90%;
  }
  &:nth-child(3) {
    width: 60%;
  }
  &:nth-child(4) {
    width: 75%;
    margin-top: 4px;
  }
  &:nth-child(5) {
    width: 85%;
  }
}

.doc-main {
  @apply flex-1 min-w-0;
}

.doc-name {
  @apply text-[13px] font-medium text-foreground truncate;
}

.doc-meta {
  @apply text-[11.5px] text-muted-foreground mt-1;
}

.doc-view {
  @apply text-xs font-medium text-foreground bg-card border border-border rounded-[var(--radius)] px-[10px] py-[6px]
    cursor-pointer inline-flex items-center gap-1 transition-colors shrink-0;

  &:hover {
    @apply bg-muted;
  }
}

/* Method selector */
.method-section {
  @apply flex flex-col gap-[10px];
}

.method-label {
  @apply text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground;
}

.method-grid {
  @apply grid grid-cols-2 gap-[10px];
}

.method-card {
  @apply flex items-center gap-3 p-[14px] rounded-[var(--radius)] border border-border bg-card
    cursor-pointer transition-all text-left w-full;

  &:disabled {
    @apply cursor-not-allowed opacity-60;
  }

  &:not(:disabled):hover {
    @apply bg-muted/40;
  }

  &[data-active="true"] {
    @apply border-foreground bg-muted/40;
    box-shadow: 0 0 0 3px hsl(var(--foreground) / 0.04);
  }
}

.method-icon {
  @apply w-9 h-9 rounded-[var(--radius)] bg-muted text-foreground inline-flex items-center justify-center shrink-0 transition-colors;
}

.method-card[data-active="true"] .method-icon {
  @apply bg-foreground text-background;
}

.method-main {
  @apply flex-1 min-w-0 flex flex-col gap-[2px];
}

.method-title {
  @apply text-[13px] font-semibold text-foreground;
}

.method-sub {
  @apply text-[11.5px] text-muted-foreground leading-[1.4];
}

.method-radio {
  @apply w-[18px] h-[18px] rounded-full border-[1.5px] border-border shrink-0 relative transition-colors;
}

.method-card[data-active="true"] .method-radio {
  @apply border-foreground;
}

.method-card[data-active="true"] .method-radio::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: 999px;
  background: hsl(var(--foreground));
}

/* Consent */
.consent {
  @apply flex items-start gap-2 cursor-pointer select-none text-[13px] leading-5 text-foreground;
}

.consent-input {
  @apply absolute opacity-0 pointer-events-none w-0 h-0;
}

.consent-box {
  @apply w-5 h-5 rounded-[var(--radius)] border border-border bg-card
    inline-flex items-center justify-center shrink-0 transition-colors;
}

.consent[data-checked="true"] .consent-box {
  @apply bg-foreground border-foreground text-background;
}

.consent-text {
  @apply flex-1;
}

/* Footer */
.foot {
  @apply mt-auto sticky bottom-0 z-10 flex items-center justify-between gap-3
    px-5 py-[14px] border-t border-border bg-card shrink-0;
}

.status {
  @apply text-xs text-muted-foreground inline-flex items-center gap-[6px];
}

.status-dot {
  @apply w-[6px] h-[6px] rounded-full bg-green-500;
}

/* Preview dialog styles живут ниже в не-scoped блоке,
   потому что DialogContent рендерится через DialogPortal в body
   и scoped-атрибуты на него не попадают. */

/* ===== Responsive ===== */

/* Mobile / phones */
@media (max-width: 768px) {
  .head {
    @apply p-4 gap-[10px];
  }

  .head-icon {
    @apply w-8 h-8;
  }

  .body {
    @apply p-4 gap-4;
  }

  .doc-preview {
    @apply p-3 gap-3;
  }

  .method-grid {
    @apply grid-cols-1;
  }

  .foot {
    @apply flex-col items-stretch gap-3 p-4;
  }
}

/* Small phones */
@media (max-width: 480px) {
  .doc-preview {
    @apply flex-wrap;
  }

  .doc-thumb {
    @apply hidden;
  }

  .doc-view {
    @apply ml-auto;
  }

  .body {
    @apply p-3 gap-[14px];
  }

  .title {
    @apply text-sm;
  }

  .sub {
    @apply text-xs;
  }

  .method-card {
    @apply p-3 gap-[10px];
  }

  .method-icon {
    @apply w-8 h-8;
  }
}
</style>
